import React, { useState } from 'react';
import { Card, Table, DatePicker, Button, Space, Tag, Typography, ConfigProvider, theme } from 'antd';
import { FileExcelOutlined, PrinterOutlined, ToolOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import useAuthStore from '../../store/useAuthStore';
import { getProductionRegister } from '../../api/reports.api';
import { formatQty, formatDate, exportToCSV } from '../../utils/formatters';

const { Text } = Typography;

/* ─────────────────────────────────────────────
   Light Theme & Compact Override Styles
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-prod-register-page {
      --bg: #f8fafc;
      --surface: #ffffff;
      --surface-2: #f1f5f9;
      --border: #e2e8f0;
      --border-hi: #cbd5e1;
      --text-1: #0f172a;
      --text-2: #334155;
      --text-3: #64748b;
      --accent: #f59e0b;
      font-family: 'Barlow', sans-serif;
    }

    .light-prod-register-page .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 6px;
      box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
    }
    
    .compact-section {
      background: #f8fafc;
      border: 1px solid var(--border);
      border-radius: 4px;
      padding: 10px 14px;
      margin-bottom: 16px;
    }

    .light-prod-register-page .ant-picker-sm,
    .light-prod-register-page .ant-btn-sm {
      border-radius: 4px;
      font-size: 12px;
    }

    /* Table Header - Not pure white */
    .light-prod-register-page .ant-table-thead > tr > th {
      background: var(--surface-2) !important;
      color: var(--text-2) !important;
      font-weight: 600;
      font-size: 11px;
      text-transform: uppercase;
      padding: 6px 10px !important;
      border-bottom: 1px solid var(--border-hi) !important;
    }

    .light-prod-register-page .ant-table-tbody > tr > td {
      padding: 6px 10px !important;
      font-size: 13px;
      color: var(--text-1);
      border-bottom: 1px solid var(--border);
    }

    /* Expanded Row Styling */
    .light-prod-register-page .ant-table-expanded-row > td {
      background: #f8fafc !important;
      padding: 12px 16px !important;
    }
    .inner-table .ant-table-thead > tr > th {
      background: #e2e8f0 !important;
      font-size: 10px;
    }

    /* Print styling to hide controls */
    @media print {
      .no-print { display: none !important; }
      .light-prod-register-page { padding: 0 !important; }
      .light-prod-register-page .ant-card { border: none !important; box-shadow: none !important; }
      .light-prod-register-page .ant-table-expanded-row { display: table-row !important; }
    }
  `}</style>
);

const S = {
  tableWrap: { border: '1px solid var(--border)', borderRadius: '6px', overflow: 'hidden' }
};

const ProductionRegisterPage = () => {
  const { user } = useAuthStore();
  const [dates, setDates] = useState([dayjs().startOf('month'), dayjs()]);

  const { data, isLoading } = useQuery({
    queryKey: ['productionRegister', user.plantID, dates],
    queryFn: () => getProductionRegister(user.plantID, { 
      from: dates[0].format('YYYY-MM-DD'), 
      to: dates[1].format('YYYY-MM-DD') 
    }),
    enabled: !!user.plantID
  });

  const columns = [
    { title: 'Date', dataIndex: 'productionDate', key: 'productionDate', render: v => <span style={{ fontWeight: 500 }}>{formatDate(v)}</span> },
    { title: 'Batch No', dataIndex: 'batchNo', key: 'batchNo', render: v => v ? <Tag bordered={false} color="blue">{v}</Tag> : <span style={{ color: 'var(--text-3)' }}>N/A</span> },
    { title: 'Ready Product', dataIndex: 'productName', key: 'productName' },
    { title: 'Qty Produced', dataIndex: 'productionQty', key: 'productionQty', render: v => <span style={{ fontWeight: 700, color: '#10b981' }}>{formatQty(v)}</span> },
    { title: 'Entry By', dataIndex: 'entryBy', key: 'entryBy', render: v => <span style={{ color: 'var(--text-3)' }}>{v}</span> }
  ];

  const expandedRowRender = (record) => {
    const consumptionColumns = [
      { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName', render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
      { title: 'Quantity Used', dataIndex: 'consumptionQty', key: 'consumptionQty', render: (v, r) => `${formatQty(v)} ${r.unitDisplay || ''}` },
      { title: 'Wastage', dataIndex: 'wastageQty', key: 'wastageQty', render: v => v > 0 ? <Text type="danger" strong>{formatQty(v)}</Text> : <span style={{ color: 'var(--text-3)' }}>-</span> },
    ];

    return (
      <div style={{ border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden', background: '#fff' }}>
        <Table 
          className="inner-table"
          columns={consumptionColumns} 
          dataSource={record.consumptionDetails || []} 
          pagination={false} 
          size="small" 
          rowKey="rawMaterialID"
        />
      </div>
    );
  };

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <LightThemeStyles />
      <div className="light-prod-register-page" style={{ padding: '8px 16px' }}>
        
        <Card
          className="report-card"
          styles={{ 
            header: { padding: '10px 16px', minHeight: 40, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }, 
            body: { padding: '16px' } 
          }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ToolOutlined style={{ color: 'var(--text-3)' }} />
              <span style={{ fontSize: 16, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-1)' }}>
                Production Register
              </span>
            </div>
          }
          extra={
            <Space className="no-print">
              <Button size="small" icon={<PrinterOutlined />} onClick={() => window.print()}>Print</Button>
              <Button size="small" type="primary" style={{ background: '#10b981' }} icon={<FileExcelOutlined />} onClick={() => exportToCSV(data || [], columns, `Production_Register_${dates[0].format('MMM-YYYY')}`)}>
                Export CSV
              </Button>
            </Space>
          }
        >
          {/* CONTROLS SECTION */}
          <div className="compact-section no-print" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>DATE RANGE :</span>
            <DatePicker.RangePicker 
              size="small" 
              value={dates} 
              onChange={(val) => setDates(val)} 
              format="DD MMM YYYY" 
              allowClear={false} 
            />
          </div>

          {/* DATA TABLE */}
          <div style={S.tableWrap}>
            <Table 
              dataSource={data || []} 
              columns={columns} 
              loading={isLoading} 
              rowKey="pid"
              expandable={{ expandedRowRender, expandRowByClick: true }}
              pagination={{ pageSize: 15, size: 'small' }}
              size="small"
              locale={{ emptyText: "No production records found for the selected dates." }}
              scroll={{ x: 600 }}
            />
          </div>

        </Card>

      </div>
    </ConfigProvider>
  );
};

export default ProductionRegisterPage;